import { JSX } from '@babel/types'
import { PrimaryButton } from '@systemeio/ui'
import React, { useState } from 'react'
import CreateModal, { CreateModalProps } from 'shared/components/create-modal'
import { Loader } from 'shared/components/loader'
import { useListingFilter } from 'shared/hooks/use-listing-filter'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { CountInterface, DataWithPaginationInterface } from 'shared/types/pagination-interface'
import { twMerge } from 'tailwind-merge'
import { BaseTableProps } from './table/components/base-table'
import { AddIcon, FilterIcon } from '@systemeio/ui/icons'

export type FilterableItemType<T> = {
  [K in keyof T]: {
    field: K
    onRender: (
      value: T[K],
      onChange: (value: T[K], state?: T) => void,
      state: T,
      onChangeState: (state: T) => void,
    ) => JSX.Element
    shouldRender?: boolean
  }
}[keyof T]

export type FiltererType<K> = {
  filter: K
  setFilter: React.Dispatch<React.SetStateAction<K>>
  defaultFilters: K
  responseFilter?: K
  setResponseFilter?: React.Dispatch<React.SetStateAction<K>>
}

export interface CreateDataInterface<T>
  extends Omit<CreateModalProps<T>, 'afterLeave' | 'opened' | 'onClose'> {
  onOpen?: () => Promise<void>
  testAttributePostfix?: string
  disabled?: boolean
  onAfterLeave?: () => void
}

export interface FilterDataInterface<T, K>
  extends Partial<Pick<BaseTableProps<T>, 'paginator' | 'limiter'>> {
  filterable: FilterableItemType<K>[]
  filterer: FiltererType<K>
  testAttributePostfix?: string
}

export interface ListingLayoutProps<T extends DataWithPaginationInterface<T['items'][0]>, K, R> {
  data: T | undefined
  title: string | JSX.Element
  titleClassName?: string
  headerClassName?: string
  counter?: CountInterface | false
  type: 'side' | 'compact' | 'default' | 'large' | 'vertical'
  children: JSX.Element
  customHeaderElement?: JSX.Element
  filterData?: FilterDataInterface<T, K>
  createData?: CreateDataInterface<R>
}

const ListingLayout = <T extends DataWithPaginationInterface<T['items'][0]>, K, R>({
  type,
  data,
  counter,
  title,
  titleClassName,
  headerClassName,
  filterData,
  createData,
  children,
  customHeaderElement,
}: ListingLayoutProps<T, K, R>) => (
  <>
    {type === 'compact' && (
      <CompactListingLayout
        data={data}
        counter={counter}
        title={title}
        titleClassName={titleClassName}
        headerClassName={headerClassName}
        filterData={filterData}
        createData={createData}
        customHeaderElement={customHeaderElement}
      >
        {children}
      </CompactListingLayout>
    )}
    {type === 'default' && (
      <DefaultListingLayout
        counter={counter}
        data={data}
        title={title}
        titleClassName={titleClassName}
        headerClassName={headerClassName}
        filterData={filterData}
        createData={createData}
        customHeaderElement={customHeaderElement}
      >
        {children}
      </DefaultListingLayout>
    )}
    {type === 'large' && (
      <LargeListingLayout
        counter={counter}
        data={data}
        title={title}
        titleClassName={titleClassName}
        headerClassName={headerClassName}
        filterData={filterData}
        createData={createData}
        customHeaderElement={customHeaderElement}
      >
        {children}
      </LargeListingLayout>
    )}
    {type === 'side' && (
      <SideListingLayout
        counter={counter}
        data={data}
        title={title}
        titleClassName={titleClassName}
        headerClassName={headerClassName}
        filterData={filterData}
        createData={createData}
        customHeaderElement={customHeaderElement}
      >
        {children}
      </SideListingLayout>
    )}
    {type === 'vertical' && (
      <VerticalListingLayout
        counter={counter}
        data={data}
        title={title}
        titleClassName={titleClassName}
        headerClassName={headerClassName}
        filterData={filterData}
        createData={createData}
        customHeaderElement={customHeaderElement}
      >
        {children}
      </VerticalListingLayout>
    )}
  </>
)

interface ChildListingLayoutProps<T extends DataWithPaginationInterface<T['items'][0]>, K, R>
  extends Pick<
    ListingLayoutProps<T, K, R>,
    | 'data'
    | 'filterData'
    | 'title'
    | 'titleClassName'
    | 'headerClassName'
    | 'createData'
    | 'children'
    | 'customHeaderElement'
    | 'counter'
  > {}

const CompactListingLayout = <T extends DataWithPaginationInterface<T['items'][0]>, K, R>({
  title,
  titleClassName,
  data,
  counter,
  filterData,
  createData,
  children,
  customHeaderElement,
}: ChildListingLayoutProps<T, K, R>) => {
  const { t } = useLocoTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const {
    isFilterShown,
    setIsFilterShown,
    handleFilter,
    setIsDefaultValuesFetching,
    isDefaultValuesFetching,
    tempState,
    setTempState,
  } = useListingFilter({ filterData })

  const WrappedTitle = () => {
    const className = `flex items-center whitespace-nowrap h-[42px] ${titleClassName || ''}`
    return typeof title === 'string' ? (
      <h1 className={className}>{title}</h1>
    ) : (
      <div className={className}>{title}</div>
    )
  }

  return (
    <>
      <div className={`mb-5 flex flex-col justify-between gap-5 lg:flex-row lg:gap-3`}>
        <div className={`flex flex-wrap justify-between truncate lg:basis-full`}>
          {data?.items.length === 0 || counter === false ? (
            <WrappedTitle />
          ) : (
            <div className={`flex items-center gap-1`}>
              {counter ? (
                <h2
                  className={`text-text-primary whitespace-nowrap text-[22px] font-bold ${
                    titleClassName || ''
                  }`}
                >
                  {counter.count}
                </h2>
              ) : (
                <Loader type={'dark'} small />
              )}
              <WrappedTitle />
            </div>
          )}
          <div className="ml-auto flex gap-3">
            {filterData && (
              <PrimaryButton
                onClick={() => setIsFilterShown(!isFilterShown)}
                className="flex items-center lg:hidden"
              >
                <FilterIcon className="h-[15px] w-[15px]" />
              </PrimaryButton>
            )}
            {customHeaderElement && (
              <div className={'flex justify-end lg:hidden'}>{customHeaderElement}</div>
            )}
            {createData && (
              <PrimaryButton
                disabled={!data}
                onClick={() => {
                  if (createData.onOpen) {
                    setIsDefaultValuesFetching(true)
                    createData.onOpen().then(() => {
                      setIsDefaultValuesFetching(false)
                      setIsModalOpen(true)
                    })
                  } else setIsModalOpen(true)
                }}
                isFetching={isDefaultValuesFetching}
                className="flex lg:hidden"
              >
                <AddIcon />
                <span className="hidden sm:flex"> {t('global.create')}</span>
              </PrimaryButton>
            )}
          </div>
        </div>
        {filterData && tempState && (
          <div
            className={`w-full flex-col overflow-x-hidden rounded-lg lg:overflow-visible ${
              isFilterShown ? 'flex' : 'hidden lg:flex'
            }`}
          >
            <div className={`bg-brand-primary-subtle flex justify-center px-6 py-4 lg:hidden`}>
              <span className="font-inter text-text-primary text-[17px] font-bold">
                {t('global.filter_by')}
              </span>
            </div>
            <form
              className={`bg-background-secondary w-full flex-col justify-end gap-3 rounded-b-lg px-6 py-4 lg:flex-row lg:bg-transparent lg:px-0 lg:py-0 ${
                isFilterShown ? 'flex' : 'hidden lg:flex'
              }`}
            >
              {filterData.filterable.map(
                el =>
                  el.shouldRender !== false &&
                  el.onRender(
                    tempState[el.field],
                    value => {
                      setTempState(prev => {
                        if (prev) {
                          return { ...prev, [el.field]: value }
                        }
                      })
                    },
                    tempState,
                    newTempState => setTempState(newTempState),
                  ),
              )}
              <PrimaryButton
                type="submit"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault()
                  handleFilter()
                }}
                className="flex min-w-[126px] items-center"
                disabled={!data}
                testAttributePostfix={filterData.testAttributePostfix}
              >
                <FilterIcon className="h-[15px] w-[15px]" />
                <span> {t('global.filter')}</span>
              </PrimaryButton>
            </form>
          </div>
        )}
        {customHeaderElement && (
          <div className={'hidden justify-end lg:flex'}>{customHeaderElement}</div>
        )}
        {createData && (
          <>
            <PrimaryButton
              disabled={createData.disabled ? createData.disabled : !data}
              onClick={() => {
                if (createData.onOpen) {
                  setIsDefaultValuesFetching(true)
                  createData.onOpen().then(() => {
                    setIsDefaultValuesFetching(false)
                    setIsModalOpen(true)
                  })
                } else setIsModalOpen(true)
              }}
              isFetching={isDefaultValuesFetching}
              className="hidden lg:flex"
              testAttributePostfix={createData.testAttributePostfix}
            >
              <AddIcon />
              <span> {t('global.create')}</span>
            </PrimaryButton>
            <CreateModal
              opened={isModalOpen}
              onClose={() => {
                setIsModalOpen(false)
              }}
              afterLeave={createData.onAfterLeave}
              {...createData}
              onCreate={data => {
                if (filterData) {
                  setTempState(filterData.filterer.defaultFilters)
                  filterData.filterer.setFilter(filterData.filterer.defaultFilters)
                }
                return createData.onCreate(data)
              }}
            />
          </>
        )}
      </div>
      {children}
    </>
  )
}

const DefaultListingLayout = <T extends DataWithPaginationInterface<T['items'][0]>, K, R>({
  title,
  data,
  counter,
  filterData,
  createData,
  children,
  customHeaderElement,
  headerClassName,
}: ChildListingLayoutProps<T, K, R>) => {
  const { t } = useLocoTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const {
    isFilterShown,
    setIsFilterShown,
    handleFilter,
    setIsDefaultValuesFetching,
    isDefaultValuesFetching,
    tempState,
    setTempState,
  } = useListingFilter({ filterData })

  return (
    <>
      <div className={`mb-5 flex flex-col justify-between gap-5 lg:gap-3 xl:flex-row`}>
        <div className={`flex justify-between gap-3 ${headerClassName}`}>
          {data?.items.length === 0 || counter === false ? (
            <h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1>
          ) : (
            <div className={`flex items-center gap-1`}>
              {counter ? (
                <h2 className={'text-text-primary whitespace-nowrap text-[22px] font-bold'}>
                  {counter.count}
                </h2>
              ) : (
                <Loader type={'dark'} small />
              )}
              <h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1>
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            {filterData && (
              <PrimaryButton
                onClick={() => setIsFilterShown(!isFilterShown)}
                className="flex items-center xl:hidden"
              >
                <FilterIcon className="h-[15px] w-[15px]" />
              </PrimaryButton>
            )}
            {customHeaderElement && <div className={'flex xl:hidden'}>{customHeaderElement}</div>}
            {createData && (
              <PrimaryButton
                disabled={createData.disabled ? createData.disabled : !data}
                onClick={() => {
                  if (createData.onOpen) {
                    setIsDefaultValuesFetching(true)
                    createData.onOpen().then(() => {
                      setIsDefaultValuesFetching(false)
                      setIsModalOpen(true)
                    })
                  } else setIsModalOpen(true)
                }}
                isFetching={isDefaultValuesFetching}
                className="flex xl:hidden"
                testAttributePostfix={createData.testAttributePostfix}
              >
                <AddIcon />
                <span className="hidden sm:flex"> {t('global.create')}</span>
              </PrimaryButton>
            )}
          </div>
        </div>
        {filterData && tempState && (
          <div
            className={`w-full flex-col overflow-x-hidden rounded-lg lg:overflow-visible ${
              isFilterShown ? 'flex' : 'hidden xl:flex'
            }`}
          >
            <div className={`bg-brand-primary-subtle flex justify-center px-6 py-4 lg:hidden`}>
              <span className="font-inter text-text-primary text-[17px] font-bold">
                {t('global.filter_by')}
              </span>
            </div>
            <form
              className={`bg-background-secondary w-full flex-col justify-end gap-3 rounded-b-lg px-6 py-4 lg:flex-row lg:bg-transparent lg:px-0 lg:py-0 ${
                isFilterShown ? 'flex' : 'hidden lg:flex'
              }`}
            >
              {filterData.filterable.map(
                el =>
                  el.shouldRender !== false &&
                  el.onRender(
                    tempState[el.field],
                    value => {
                      setTempState(prev => {
                        if (prev) {
                          return { ...prev, [el.field]: value }
                        }
                      })
                    },
                    tempState,
                    newTempState => setTempState(newTempState),
                  ),
              )}
              <PrimaryButton
                type="submit"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault()
                  handleFilter()
                }}
                className="flex items-center"
                disabled={!data}
                testAttributePostfix={filterData.testAttributePostfix}
              >
                <FilterIcon className="h-[15px] w-[15px]" />
                <span> {t('global.filter')}</span>
              </PrimaryButton>
            </form>
          </div>
        )}
        {customHeaderElement && <div className={'hidden xl:flex'}>{customHeaderElement}</div>}
        {createData && (
          <>
            <PrimaryButton
              disabled={!data}
              onClick={() => {
                if (createData.onOpen) {
                  setIsDefaultValuesFetching(true)
                  createData.onOpen().then(() => {
                    setIsDefaultValuesFetching(false)
                    setIsModalOpen(true)
                  })
                } else setIsModalOpen(true)
              }}
              isFetching={isDefaultValuesFetching}
              className="hidden xl:flex"
            >
              <AddIcon />
              <span> {t('global.create')}</span>
            </PrimaryButton>
            <CreateModal
              caption={createData.caption}
              opened={isModalOpen}
              requiredFields={createData.requiredFields}
              onClose={() => {
                setIsModalOpen(false)
              }}
              afterLeave={createData.onAfterLeave}
              defaultValues={createData.defaultValues}
              creatable={createData.creatable}
              onCreate={data => createData.onCreate(data)}
            />
          </>
        )}
      </div>
      {children}
    </>
  )
}

const SideListingLayout = <T extends DataWithPaginationInterface<T['items'][0]>, K, R>({
  title,
  data,
  counter,
  filterData,
  createData,
  children,
  customHeaderElement,
  headerClassName,
}: ChildListingLayoutProps<T, K, R>) => {
  const { t } = useLocoTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const {
    isFilterShown,
    setIsFilterShown,
    handleFilter,
    setIsDefaultValuesFetching,
    isDefaultValuesFetching,
    tempState,
    setTempState,
  } = useListingFilter({ filterData })

  return (
    <>
      <div className={`mb-5 flex justify-between`}>
        {data?.items.length === 0 || counter === false ? (
          <h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1>
        ) : (
          <div className={twMerge('flex items-center gap-1', headerClassName)}>
            {counter ? (
              <h2 className={'text-text-primary whitespace-nowrap text-[22px] font-bold'}>
                {counter.count}
              </h2>
            ) : (
              <Loader type={'dark'} small />
            )}
            <h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1>
          </div>
        )}
        <div className="flex gap-5 md:gap-3">
          {filterData && (
            <PrimaryButton
              onClick={() => setIsFilterShown(!isFilterShown)}
              className="flex items-center xl:hidden"
            >
              <FilterIcon className="h-[15px] w-[15px]" />
            </PrimaryButton>
          )}
          {customHeaderElement && <div>{customHeaderElement}</div>}
          {createData && (
            <>
              <PrimaryButton
                disabled={createData.disabled ? createData.disabled : !data}
                onClick={() => {
                  if (createData.onOpen) {
                    setIsDefaultValuesFetching(true)
                    createData.onOpen().then(() => {
                      setIsDefaultValuesFetching(false)
                      setIsModalOpen(true)
                    })
                  } else setIsModalOpen(true)
                }}
                isFetching={isDefaultValuesFetching}
                testAttributePostfix={createData.testAttributePostfix}
              >
                <AddIcon />
                <span className="hidden sm:flex"> {t('global.create')}</span>
              </PrimaryButton>
              <CreateModal
                caption={createData.caption}
                opened={isModalOpen}
                requiredFields={createData.requiredFields}
                onClose={() => {
                  setIsModalOpen(false)
                }}
                afterLeave={createData.onAfterLeave}
                defaultValues={createData.defaultValues}
                creatable={createData.creatable}
                onCreate={data => createData.onCreate(data)}
              />
            </>
          )}
        </div>
      </div>
      {filterData && tempState && (
        <div className={`flex flex-col gap-0 xl:flex-row xl:gap-5`}>
          <div
            className={`flex w-full flex-col overflow-x-hidden rounded-lg lg:overflow-visible xl:w-[300px] xl:overflow-x-hidden`}
          >
            <div
              className={`bg-brand-primary-subtle justify-center px-6 py-4 ${
                isFilterShown ? 'flex lg:hidden xl:flex' : 'hidden xl:flex'
              }`}
            >
              <span className="font-inter text-text-primary text-[17px] font-bold">
                {t('global.filter_by')}
              </span>
            </div>
            <form
              className={twMerge(
                'bg-background-secondary xl:bg-background-secondary mb-5 flex w-full flex-col flex-wrap justify-start gap-3 rounded-b-lg px-6 py-4 lg:flex-row lg:bg-transparent lg:px-0 lg:py-0 xl:mb-0 xl:flex-col xl:flex-nowrap xl:justify-end xl:px-2 xl:py-4',
                isFilterShown ? 'flex' : 'hidden xl:flex',
              )}
            >
              {filterData.filterable.map(
                el =>
                  el.shouldRender !== false &&
                  el.onRender(
                    tempState[el.field],
                    value => {
                      setTempState(prev => {
                        if (prev) {
                          return { ...prev, [el.field]: value }
                        }
                      })
                    },
                    tempState,
                    newTempState => setTempState(newTempState),
                  ),
              )}
              <PrimaryButton
                type="submit"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault()
                  handleFilter()
                }}
                className="flex items-center"
                disabled={!data}
                testAttributePostfix={filterData.testAttributePostfix}
              >
                <FilterIcon className="h-[15px] w-[15px]" />
                <span> {t('global.filter')}</span>
              </PrimaryButton>
            </form>
          </div>
          <div className={'xl:w-[calc(100%-300px-20px)]'}>{children}</div>
        </div>
      )}
      {!filterData && children}
    </>
  )
}

const LargeListingLayout = <T extends DataWithPaginationInterface<T['items'][0]>, K, R>({
  title,
  data,
  counter,
  filterData,
  createData,
  children,
  customHeaderElement,
}: ChildListingLayoutProps<T, K, R>) => {
  const { t } = useLocoTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const {
    isFilterShown,
    setIsFilterShown,
    handleFilter,
    setIsDefaultValuesFetching,
    isDefaultValuesFetching,
    tempState,
    setTempState,
  } = useListingFilter({ filterData })

  return (
    <>
      <div className={`mb-5 flex justify-between`}>
        {data?.items.length === 0 || counter === false ? (
          <h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1>
        ) : (
          <div className={`flex items-center gap-1`}>
            {counter ? (
              <h2 className={'text-text-primary whitespace-nowrap text-[22px] font-bold'}>
                {counter.count}
              </h2>
            ) : (
              <Loader type={'dark'} small />
            )}
            <h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1>
          </div>
        )}
        <div className="flex gap-5 md:gap-3">
          {filterData && (
            <PrimaryButton
              onClick={() => setIsFilterShown(!isFilterShown)}
              className="flex items-center lg:hidden"
              testAttributePostfix={filterData.testAttributePostfix}
            >
              <FilterIcon className="h-[15px] w-[15px]" />
            </PrimaryButton>
          )}
          {customHeaderElement && <div>{customHeaderElement}</div>}
          {createData && (
            <>
              <PrimaryButton
                disabled={!data}
                onClick={() => {
                  if (createData.onOpen) {
                    setIsDefaultValuesFetching(true)
                    createData.onOpen().then(() => {
                      setIsDefaultValuesFetching(false)
                      setIsModalOpen(true)
                    })
                  } else setIsModalOpen(true)
                }}
                isFetching={isDefaultValuesFetching}
                testAttributePostfix={createData.testAttributePostfix}
              >
                <AddIcon />
                <span className="hidden sm:flex"> {t('global.create')}</span>
              </PrimaryButton>
              <CreateModal
                caption={createData.caption}
                opened={isModalOpen}
                requiredFields={createData.requiredFields}
                onClose={() => {
                  setIsModalOpen(false)
                }}
                afterLeave={createData.onAfterLeave}
                defaultValues={createData.defaultValues}
                creatable={createData.creatable}
                onCreate={data => {
                  setTempState(undefined!)
                  return createData.onCreate(data)
                }}
              />
            </>
          )}
        </div>
      </div>
      {filterData && tempState && (
        <div className={`flex w-full flex-col overflow-x-hidden rounded-lg lg:overflow-visible`}>
          <div
            className={twMerge(
              'bg-brand-primary-subtle justify-center px-6 py-4',
              isFilterShown ? 'flex lg:hidden' : 'hidden',
            )}
          >
            <span className="font-inter text-text-primary text-[17px] font-bold">
              {t('global.filter_by')}
            </span>
          </div>
          <form
            className={twMerge(
              'bg-background-secondary mb-5  flex w-full flex-col justify-end gap-3 rounded-b-lg px-6 py-4 lg:flex-row lg:bg-transparent lg:px-0 lg:py-0',
              isFilterShown ? 'flex' : 'hidden lg:flex',
            )}
          >
            {filterData.filterable.map(
              el =>
                el.shouldRender !== false &&
                el.onRender(
                  tempState[el.field],
                  value => {
                    setTempState(prev => {
                      if (prev) {
                        return { ...prev, [el.field]: value }
                      }
                    })
                  },
                  tempState,
                  newTempState => setTempState(newTempState),
                ),
            )}
            <PrimaryButton
              type="submit"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                handleFilter()
              }}
              className="flex items-center"
              disabled={!data}
            >
              <FilterIcon className="h-[15px] w-[15px]" />
              <span> {t('global.filter')}</span>
            </PrimaryButton>
          </form>
        </div>
      )}
      {children}
    </>
  )
}

const VerticalListingLayout = <T extends DataWithPaginationInterface<T['items'][0]>, K, R>({
  title,
  data,
  counter,
  filterData,
  createData,
  children,
  customHeaderElement,
}: ChildListingLayoutProps<T, K, R>) => {
  const { t } = useLocoTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const {
    isFilterShown,
    setIsFilterShown,
    handleFilter,
    setIsDefaultValuesFetching,
    isDefaultValuesFetching,
    tempState,
    setTempState,
  } = useListingFilter({ filterData })

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap justify-between gap-3 lg:flex-col lg:gap-5">
        {data?.items.length === 0 || counter === false ? (
          <h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1>
        ) : (
          <div className="flex items-center gap-1">
            {counter ? (
              <h2 className={'text-darkblue whitespace-nowrap text-[22px] font-bold'}>
                {counter.count}
              </h2>
            ) : (
              <Loader type={'dark'} small />
            )}
            <h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1>
          </div>
        )}

        <div
          className={twMerge(
            'flex gap-5 md:gap-3 lg:hidden',
            (customHeaderElement || createData) && 'flex',
          )}
        >
          {filterData && (
            <PrimaryButton
              onClick={() => setIsFilterShown(!isFilterShown)}
              className="flex items-center lg:hidden"
              testAttributePostfix={filterData.testAttributePostfix}
            >
              <FilterIcon className="h-[15px] w-[15px]" />
            </PrimaryButton>
          )}
          {customHeaderElement && <div>{customHeaderElement}</div>}
          {createData && (
            <>
              <PrimaryButton
                disabled={!data}
                onClick={() => {
                  if (createData.onOpen) {
                    setIsDefaultValuesFetching(true)
                    createData.onOpen().then(() => {
                      setIsDefaultValuesFetching(false)
                      setIsModalOpen(true)
                    })
                  } else setIsModalOpen(true)
                }}
                isFetching={isDefaultValuesFetching}
                testAttributePostfix={createData.testAttributePostfix}
              >
                <AddIcon />
                <span className="hidden sm:flex"> {t('global.create')}</span>
              </PrimaryButton>
              <CreateModal
                caption={createData.caption}
                opened={isModalOpen}
                requiredFields={createData.requiredFields}
                onClose={() => {
                  setIsModalOpen(false)
                }}
                afterLeave={createData.onAfterLeave}
                defaultValues={createData.defaultValues}
                creatable={createData.creatable}
                onCreate={data => {
                  setTempState(undefined!)
                  return createData.onCreate(data)
                }}
              />
            </>
          )}
        </div>
      </div>
      {filterData && tempState && (
        <div
          className={twMerge(
            'flex w-full flex-col overflow-x-hidden rounded-lg lg:overflow-visible',
            !isFilterShown && 'hidden lg:flex',
          )}
        >
          <div
            className={twMerge(
              'bg-brand-primary-subtle justify-center px-6 py-4',
              isFilterShown ? 'flex lg:hidden' : 'hidden',
            )}
          >
            <span className="font-inter text-darkblue text-[17px] font-bold">
              {t('global.filter_by')}
            </span>
          </div>
          <form
            className={twMerge(
              'flex w-full  flex-col justify-end gap-3 rounded-b-lg bg-white px-6 py-4 lg:flex-row lg:bg-transparent lg:px-0 lg:py-0',
              isFilterShown ? 'flex' : 'hidden lg:flex',
            )}
          >
            {filterData.filterable.map(
              el =>
                el.shouldRender !== false &&
                el.onRender(
                  tempState[el.field],
                  value => {
                    setTempState(prev => {
                      if (prev) {
                        return { ...prev, [el.field]: value }
                      }
                    })
                  },
                  tempState,
                  newTempState => setTempState(newTempState),
                ),
            )}
            <PrimaryButton
              type="submit"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                handleFilter()
              }}
              className="flex items-center"
              disabled={!data}
            >
              <FilterIcon className="h-[15px] w-[15px]" />
              <span> {t('global.filter')}</span>
            </PrimaryButton>
          </form>
        </div>
      )}
      {children}
    </div>
  )
}

export default ListingLayout
